import { Languages } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LOCALES, setLocale, type Locale } from "../i18n";
import { cn } from "./cn";

const LABELS: Record<Locale, string> = {
  en: "EN",
  fr: "FR",
};

export interface LanguageSelectorProps {
  className?: string;
  /** Mirrors the choice onto the account record so it follows the user. */
  onChange?: (locale: Locale) => void;
}

/**
 * Switches language in place. The Vaadin original wrote a cookie and reloaded
 * the page, which threw away filters and unsaved edits; this just swaps the
 * bundle.
 */
export function LanguageSelector({ className, onChange }: LanguageSelectorProps) {
  const { i18n, t } = useTranslation();
  const current = (i18n.language.split("-")[0] ?? "en") as Locale;

  return (
    <label className={cn("relative inline-flex items-center", className)}>
      <Languages className="pointer-events-none absolute left-2 size-4 text-ink-faint" aria-hidden="true" />
      <span className="sr-only">{t("language.label", "Language")}</span>
      <select
        value={current}
        onChange={(event) => {
          const next = event.target.value as Locale;
          void setLocale(next);
          onChange?.(next);
        }}
        className={cn(
          "h-8 appearance-none rounded-md border border-line bg-surface pl-7 pr-6 text-[13px] font-medium text-ink",
          "focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-accent max-md:h-9",
        )}
      >
        {SUPPORTED_LOCALES.map((locale) => (
          <option key={locale} value={locale}>
            {LABELS[locale]}
          </option>
        ))}
      </select>
    </label>
  );
}
